import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import { NGIcon, GhIcon, KESIcon, USIcon, USDCIcon, USDTIcon, GBicon } from "asset/icons";
import walletFeeIllustration from "asset/icons/wallet.svg";
import { Dialog, DialogContent } from "../ui/Dialog";
import { Button } from "../ui/Button";
import { Skeleton } from "../ui/Skeleton";
import { handleGetActiveMaintenanceFees } from "api/onboarding";
import { currencyToDisplay } from "../flex/utils";
import { cn } from "../../lib/utils";
import QuestionHelpIcon from "../QuestionHelpIcon";
import currencyPairCheck from "../../utilities/currencyPairCheck";

export default function WalletMaintenanceFeeModal({ isOpen = true, onClose, onLearnMore }) {
  const { business } = useSelector((s) => s || {});
  const [isError, setIsError] = useState(false)

  // Fetch active maintenance fees
  const { data: activeFeesData, isLoading } = useQuery({
    queryKey: ["activeMaintenanceFees"],
    queryFn: handleGetActiveMaintenanceFees,
    enabled: !!business?._id && Boolean(isOpen),
    onSettled: (data) => {
      if (data?.statusCode && String(data?.statusCode).startsWith('4')) {
        setIsError(true)
      } else {
        setIsError(false)
      }
    }
  });

  // Ensure NGN always appears first in the list
  const sortNgnFirst = (list) =>
    [...list].sort((a, b) => {
      const aIsNgn = String(a.key || a.currency).toUpperCase().includes("NGN");
      const bIsNgn = String(b.key || b.currency).toUpperCase().includes("NGN");
      if (aIsNgn && !bIsNgn) return -1;
      if (!aIsNgn && bIsNgn) return 1;
      return 0;
    });

  const feeSchedule = React.useMemo(() => {
    const rawList = activeFeesData?.data || activeFeesData;
    if (Array.isArray(rawList) && rawList.length > 0) {
      const parsed = rawList?.map((item) => {
        const curr = String(item.currency || item.code || item.name || "").toUpperCase();
        const val = item?.isCapped ? item?.cappedValue : item.amount ?? "";
        return {
          ...item,
          currency: item.currency || item.code || item.name || curr,
          rawCurrency: curr,
          fee: val,
        };
      });

      const usdKeys = ["USD", "USDC", "USDT"];
      const usdItems = parsed.filter((item) => usdKeys.includes(item.rawCurrency));

      if (usdItems.length > 0) {
        const firstFeeVal = String(usdItems[0].fee).trim();
        const allUsdSameValue = usdItems.every(
          (item) => String(item.fee).trim() === firstFeeVal
        );

        if (allUsdSameValue) {
          const nonUsdItems = parsed.filter((item) => !usdKeys.includes(item.rawCurrency));

          let feeDisplay = firstFeeVal;
          if (!feeDisplay.includes("$") && !feeDisplay.includes("%") && !feeDisplay.includes("₦") && !feeDisplay.includes("GH₵") && !feeDisplay.includes("KSh")) {
            const num = Number(feeDisplay.replace(/[^0-9.]/g, ""));
            feeDisplay = !isNaN(num) && num > 0 ? `$${num}` : `$${firstFeeVal}`;
          }

          const mergedUsdObject = {
            currency: "USD, USDC & USDT",
            fee: feeDisplay,
            key: "USD_COMBO",
          };

          return sortNgnFirst([
            ...nonUsdItems?.map((item) => ({
              currency: item.currency,
              fee: item.fee,
              key: item.rawCurrency || item.currency,
            })),
            mergedUsdObject,
          ]);
        }
      }

      return sortNgnFirst(parsed?.map((item) => ({
        currency: currencyPairCheck(item.currency),
        fee: item.fee,
        key: item.rawCurrency || item.currency,
      })));
    }
  }, [activeFeesData]);

  const renderCurrencyIcon = (key, currencyName) => {
    const normKey = String(key || currencyName).toUpperCase();

    if (normKey.includes("COMBO") || normKey.includes("&") || normKey.includes("USD, USDC")) {
      return (
        <div className="flex items-center -space-x-1.5 flex-shrink-0">
          <div className="size-5 rounded-full overflow-hidden flex items-center justify-center z-30">
            <USIcon className="w-full h-full object-cover" />
          </div>
          <div className="size-6 rounded-full overflow-hidden flex items-center justify-center z-20">
            <USDCIcon className="w-full h-full object-cover" />
          </div>
          <div className="size-5 rounded-full overflow-hidden flex items-center justify-center z-10">
            <USDTIcon className="w-full h-full object-cover" />
          </div>
        </div>
      );
    }
    if (normKey === "USDC") {
      return (
        <div className="w-5 h-5 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0">
          <USDCIcon className="w-full h-full object-cover" />
        </div>
      );
    }
    if (normKey === "USDT") {
      return (
        <div className="w-5 h-5 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0">
          <USDTIcon className="w-full h-full object-cover" />
        </div>
      );
    }
    if (normKey.includes("NGN") || normKey.includes("NG")) {
      return (
        <div className="w-5 h-5 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0">
          <NGIcon className="w-full h-full object-cover" />
        </div>
      );
    }
    if (normKey.includes("GHC") || normKey.includes("GH")) {
      return (
        <div className="w-5 h-5 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0">
          <GhIcon className="w-full h-full object-cover" />
        </div>
      );
    }
    if (normKey.includes("KES") || normKey.includes("KE")) {
      return (
        <div className="w-5 h-5 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0">
          <KESIcon className="w-full h-full object-cover" />
        </div>
      );
    }
    if (normKey.includes("GBP") || normKey.includes("GB")) {
      return (
        <div className="w-5 h-5 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0">
          <GBicon className="w-full h-full object-cover" />
        </div>
      );
    }
    return (
      <div className="w-5 h-5 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0">
        <USIcon className="w-full h-full object-cover" />
      </div>
    );
  };

  const renderFeeValue = (item) => {
    const feeStr = String(item.fee || "");
    if (
      feeStr.includes("$") ||
      feeStr.includes("₦") ||
      feeStr.includes("GH₵") ||
      feeStr.includes("KSh") ||
      feeStr.includes("%") ||
      item.currency === "USD, USDC & USDT"
    ) {
      return feeStr;
    }
    const num = Number(item.fee);
    if (!isNaN(num)) {
      return item?.currency === 'NGN' ? `0.3% capped at ${currencyToDisplay(item?.currency, item?.fee)}` : currencyToDisplay(item.currency, num);
    }
    return feeStr;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <DialogContent onInteractOutside={(e) => e.preventDefault()} className="max-w-[700px] p-0 rounded-[12px] w-11/12 sm:w-full outline-none ring-0 border-none overflow-hidden sm:max-w-[740px]  bg-white">
        <div className="flex flex-col md:flex-row w-full overflow-hidden">
          {/* Left Side Visual Illustration using wallet.svg from asset/icons */}
          <div className="w-full md:w-[50%] bg-[#F3EFFC] hidden sm:flex items-center justify-center relative min-h-[240px] md:min-h-[386px] overflow-hidden">
            <img
              src={walletFeeIllustration}
              alt="Wallet Maintenance Fees"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Right Side Content & Fee Schedule */}
          <div className="w-full md:w-[50%] p-6 md:p-6 flex flex-col justify-between bg-white space-y-4 text-left">
            <div className="space-y-4">
              {/* Title */}
              <p className="text-2xl font-semibold leading-8 text-[#020618] mb-3">
                Introducing wallet maintenance fees
              </p>

              <div className="text-[#45556C] pb-3 space-y-3 text-sm font-[350] leading-6">
                {/* Description Paragraph 1 */}
                <p className="text-sm font-[350] leading-6" style={{
                  lineHeight: "24px !important",
                  letterSpacing: "0% !important"
                }}>
                  A monthly maintenance fee will apply to all new and existing wallets. This fee helps support the continued provision of payment services.
                </p>

                {/* Description Paragraph 2 */}
                <p className="aku-body-small-regular" style={{
                  lineHeight: "24px !important",
                  letterSpacing: "0% !important"
                }}>
                  The fee will be charged automatically on the first day of each month.
                </p>
              </div>

              {/* Fee Schedule */}
              <div className="space-y-2">
                <p className="text-sm font-[450] leading-5 text-[#020618]">
                  Fee schedule
                </p>

                {isLoading ? (
                  <div className="bg-[#FBFAFF] rounded-lg p-3 space-y-3 border border-[#EBE4FF]">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="flex items-center justify-between py-0.5">
                        <div className="flex items-center gap-2">
                          <Skeleton className="w-5 h-5 rounded-full bg-gray-200" />
                          <Skeleton className="w-16 h-4 rounded bg-gray-200" />
                        </div>
                        <Skeleton className="w-24 h-4 rounded bg-gray-200" />
                      </div>
                    ))}
                  </div>
                ) : isError || activeFeesData?.status === false || activeFeesData?.error ? (
                    <div className="bg-[#FBFAFF] rounded-lg p-3 space-y-3 border border-[#EBE4FF]">
                      {[1, 2, 3, 4]?.map((_, index) => (
                        <div key={index} className="flex items-center justify-between text-xs md:text-[13px]">
                          <div className="flex items-center gap-2">
                            <Skeleton className="w-5 h-5 rounded-full bg-gray-200" />
                            <Skeleton className="w-16 h-4 rounded bg-gray-200" />
                          </div>
                          <Skeleton className="w-24 h-4 rounded bg-gray-200" />
                        </div>
                      ))}
                    </div>
                ) : (
                      <div className="bg-[#FBFAFF] rounded-lg p-3 space-y-3 border border-[#EBE4FF]">
                        {feeSchedule?.map((item, index) => (
                      <div key={index} className="flex items-center justify-between text-xs md:text-[13px]">
                            <div className="flex items-center gap-1">
                          {renderCurrencyIcon(item.key || item.currency, item.currency)}
                              <span className="font-[500] text-sm leading-6 text-[#101828]">{item.currency}</span>
                        </div>
                            <span className="text-[#494E6E] text-right flex items-baseline font-[400] text-sm leading-[140%] gap-1">
                              {renderFeeValue(item)} {item.currency === 'NGN' ?
                                <span className=""><QuestionHelpIcon details={`0.3% of your transfers, capped at ${currencyToDisplay(item.currency, item.fee)} monthly`} /></span> : null}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              {/* To be uncommented when the link is provided */}
              {isError ? <Button
                variant="destructive"
                onClick={onClose}
                className="text-[#344054] hover:text-[#101828] text-xs md:text-sm font-medium"
              >
                Close
              </Button> : (
                <Button
                type="button"
                variant="ghost"
                    onClick={onLearnMore}
                className="text-[#344054] hover:text-[#101828] text-xs md:text-sm font-medium"
              >
                Learn more
                </Button>
              )}
              <Button
                type="button"
                disabled={isError}
                onClick={onClose}
                className={cn("!bg-[#6F00FF] hover:!bg-[#5e00db] text-white text-xs md:text-sm font-medium px-6 py-2.5 rounded-lg transition-all shadow-sm", isError && "!bg-gray-300 !hover:bg-gray-300 cursor-not-allowed")}
              >
                Got it
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
